import { Renderer } from './core/Renderer';
import { GameScene } from './core/Scene';
import { TopDownCamera } from './core/Camera';
import { Loop } from './core/Loop';
import { InputManager } from './input/InputManager';
import { Projector } from './input/Projector';
import { Ground } from './world/Ground';
import { Grid } from './world/Grid';
import { GameState } from './game/GameState';
import { Cookie } from './game/Cookie';
import { CookieMonster } from './game/CookieMonster';
import { TrashCan } from './game/TrashCan';
import { CookieSpawner } from './game/CookieSpawner';
import { HUD } from './ui/HUD';
import { DEFAULT_CONFIG, type GameConfig } from './types/GameConfig';
import type { Entity } from './types/Entity';

export class Game {
  private readonly config: GameConfig;
  private readonly scene: GameScene;
  private readonly camera: TopDownCamera;
  private readonly loop: Loop;

  // Initialized in start()
  private renderer!: Renderer;
  private input!: InputManager;
  private projector!: Projector;
  private ground!: Ground;
  private grid!: Grid;
  private trashCan!: TrashCan;
  private spawner!: CookieSpawner;
  private gameState!: GameState;
  private hud!: HUD;

  private readonly cookies: Cookie[] = [];
  private readonly monsters: CookieMonster[] = [];
  // Generic entity array for future systems; not used by cookies/monsters
  private readonly entities: Entity[] = [];

  // Tap / drag disambiguation
  private dragActive = false;
  private dragLastNdcX = 0;
  private dragLastNdcY = 0;
  private tapStartScreenX = 0;
  private tapStartScreenY = 0;
  private static readonly TAP_THRESHOLD_PX = 10;

  constructor(config: Partial<GameConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.scene = new GameScene(this.config.backgroundColor);
    this.camera = new TopDownCamera(this.config);
    this.loop = new Loop(this.update.bind(this));
  }

  start(): void {
    const canvas = document.getElementById(this.config.canvasId);
    if (!(canvas instanceof HTMLCanvasElement)) {
      throw new Error(`Canvas #${this.config.canvasId} not found`);
    }

    this.renderer = new Renderer(this.config.canvasId, (w, h) => {
      this.camera.updateAspect(w, h);
    });

    this.input = new InputManager(canvas);
    this.projector = new Projector(this.camera.camera);

    this.ground = new Ground(this.config.groundSize);
    this.ground.addTo(this.scene);

    this.grid = new Grid(this.config.groundSize, this.config.groundSize);
    this.grid.setVisible(this.config.showGrid);
    this.grid.addTo(this.scene);

    this.trashCan = new TrashCan(this.config.trashCanCatchRadius);
    this.trashCan.addTo(this.scene);

    this.gameState = new GameState(this.config, this.config.startingCookies);

    this.spawner = new CookieSpawner(this.config, (cookie) => {
      this.cookies.push(cookie);
      cookie.addToScene(this.scene);
    });

    this.hud = new HUD();

    this.wireInput();
    this.loop.start();
  }

  stop(): void {
    this.loop.stop();
    this.input.dispose();

    for (const cookie of this.cookies) {
      cookie.removeFromScene(this.scene);
      cookie.dispose();
    }
    this.cookies.length = 0;

    for (const monster of this.monsters) {
      monster.removeFromScene(this.scene);
      monster.dispose();
    }
    this.monsters.length = 0;

    for (const entity of this.entities) entity.dispose();
    this.entities.length = 0;

    this.trashCan.dispose();
    this.hud.dispose();
    this.ground.dispose();
    this.grid.dispose();
    this.renderer.dispose();
    this.scene.dispose();
  }

  addEntity(entity: Entity): void {
    this.entities.push(entity);
    entity.addToScene(this.scene);
  }

  removeEntity(entity: Entity): void {
    const idx = this.entities.indexOf(entity);
    if (idx !== -1) {
      this.entities.splice(idx, 1);
      entity.removeFromScene(this.scene);
    }
  }

  private update(delta: number, _elapsed: number): void {
    for (const entity of this.entities) entity.update(delta);
    for (const monster of this.monsters) monster.update(delta);

    if (!this.gameState.isGameOver) {
      this.spawner.update(delta);
    }

    this.processCookies(delta);

    this.trashCan.updateFill(this.gameState.trashCount, this.config.trashCanCapacity);

    this.hud.update(
      this.gameState.playerCookies,
      this.gameState.nextMonsterCost,
      this.gameState.trashCount,
      this.config.trashCanCapacity,
      this.gameState.canAffordMonster(),
    );

    this.renderer.render(this.scene.scene, this.camera.camera);
  }

  private processCookies(delta: number): void {
    const catchR2 = this.trashCan.catchRadius * this.trashCan.catchRadius;
    const toRemove: Cookie[] = [];

    for (const cookie of this.cookies) {
      cookie.update(delta);

      const cx = cookie.position.x;
      const cz = cookie.position.z;

      if (cx * cx + cz * cz <= catchR2) {
        toRemove.push(cookie);
        if (this.gameState.trashCookie()) {
          this.hud.showGameOver(this.gameState.totalCookiesEaten);
          this.loop.pause();
        }
        continue;
      }

      for (const monster of this.monsters) {
        if (monster.canEat(cookie)) {
          toRemove.push(cookie);
          this.gameState.eatCookie();
          break;
        }
      }
    }

    for (const cookie of toRemove) {
      const idx = this.cookies.indexOf(cookie);
      if (idx !== -1) this.cookies.splice(idx, 1);
      cookie.removeFromScene(this.scene);
      cookie.dispose();
    }
  }

  private wireInput(): void {
    const canvas = document.getElementById(this.config.canvasId) as HTMLCanvasElement;

    canvas.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      this.camera.zoom(e.deltaY * 0.01);
    }, { passive: false });

    this.input.on('pinch', (e) => {
      if (e.pinchDelta !== undefined) {
        this.camera.zoom(-e.pinchDelta * 0.05);
      }
    });

    this.input.on('start', (e) => {
      this.dragActive = true;
      this.dragLastNdcX = e.ndcX;
      this.dragLastNdcY = e.ndcY;
      this.tapStartScreenX = e.screenX;
      this.tapStartScreenY = e.screenY;
    });

    this.input.on('move', (e) => {
      if (!this.dragActive) return;
      const worldA = this.projector.toWorld(this.dragLastNdcX, this.dragLastNdcY);
      const worldB = this.projector.toWorld(e.ndcX, e.ndcY);
      if (worldA && worldB) {
        this.camera.pan(worldA.x - worldB.x, worldA.z - worldB.z);
        this.camera.clampToBounds(this.config.groundSize / 2);
      }
      this.dragLastNdcX = e.ndcX;
      this.dragLastNdcY = e.ndcY;
    });

    this.input.on('end', (e) => {
      if (this.dragActive) {
        const dx = e.screenX - this.tapStartScreenX;
        const dy = e.screenY - this.tapStartScreenY;
        if (dx * dx + dy * dy < Game.TAP_THRESHOLD_PX ** 2) {
          this.handleTap(e.ndcX, e.ndcY);
        }
      }
      this.dragActive = false;
    });

    this.input.on('cancel', () => { this.dragActive = false; });
  }

  private handleTap(ndcX: number, ndcY: number): void {
    if (!this.gameState.canAffordMonster()) return;

    const worldPos = this.projector.toWorld(ndcX, ndcY);
    if (!worldPos) return;

    // Block placement directly on the trash can
    if (worldPos.x * worldPos.x + worldPos.z * worldPos.z < 1.5 * 1.5) return;

    this.gameState.buyMonster();
    const monster = new CookieMonster(worldPos, this.config.monsterEatRadius);
    this.monsters.push(monster);
    monster.addToScene(this.scene);
  }
}
